import { Banner, Button, Dialog, LoadingSpinner, Select, TextArea, TextInput } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import { DATA_API_AUTH_PROVIDER_TYPE, type DataApiBatchResult, graphqlApi, useActiveProject } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { Center } from '@nx/ui';
import RunnableCodeBlock from '@nx/ui/src/runnables/runnable-code-block';
import { useCallback, useEffect, useState } from 'react';

import { ApiErrorBanner } from './api-error-banner';

interface CurlCommandModalProps {
  dataApi: DataApiBatchResult;
  onClose: () => void;
}

const defaultQuery = '<YOUR_GRAPHQL_QUERY>';
const defaultAPIKey = '<YOUR_API_KEY>';
const defaultJWT = '<YOUR_JWT>';

const logger = createLogger(APP_SCOPE.dataApi);

type AuthProviderValue = {
  id: string;
  type: DATA_API_AUTH_PROVIDER_TYPE;
  value: string;
  name: string;
  enabled: boolean;
};

export const CurlCommandModal = ({ dataApi, onClose }: CurlCommandModalProps) => {
  const activeProject = useActiveProject();
  const [getAuthProvidersBatch, { isLoading: isLoadingAuthProviderBatch, error: errorAuthProviderBatch }] =
    graphqlApi.useLazyGetAuthProvidersBatchQuery();
  const [query, setQuery] = useState('');
  const [authValues, setAuthValues] = useState<AuthProviderValue[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [chosenProviderId, setChosenProviderId] = useState<string | null>(null);
  const chosenProvider = authValues.find((authValue) => authValue.id === chosenProviderId);

  const getAuthData = useCallback(() => {
    setIsInitialized(false);
    getAuthProvidersBatch({
      projectId: activeProject.id,
      instanceId: dataApi.instanceId,
      dataApiId: dataApi.id,
    })
      .unwrap()
      .then((authProvidersResponse) => {
        const mappedAuthValues = authProvidersResponse.map((provider) => {
          return {
            id: provider.id,
            type: provider.type,
            value: '',
            name: provider.name,
            enabled: provider.enabled,
          };
        });
        setAuthValues(mappedAuthValues);
        if (mappedAuthValues.length === 1 && mappedAuthValues[0]) {
          setChosenProviderId(mappedAuthValues[0].id);
        }
        setIsInitialized(true);
      })
      .catch((err) => {
        logger.error(err);
      });
  }, [dataApi, activeProject.id, getAuthProvidersBatch]);

  useEffect(() => {
    getAuthData();
  }, [getAuthData]);

  let authLines = '';
  if (chosenProvider) {
    if (chosenProvider.type === DATA_API_AUTH_PROVIDER_TYPE.jwks) {
      authLines = `\n  --header 'Authorization: Bearer ${chosenProvider.value || defaultJWT}' \\`;
    } else {
      authLines = `\n  --header 'x-api-key: ${chosenProvider.value || defaultAPIKey}' \\`;
    }
  }

  const handleUpdateAuthValue = (id: string, value: string) => {
    setAuthValues((prevValues) =>
      prevValues.map((authValue) => (authValue.id === id ? { ...authValue, value } : authValue)),
    );
  };

  const curlCommand = `curl --location ${dataApi.url} \\
  --header 'Content-Type: application/json' \\${authLines}
  --data '{"query": "${query.replace(/{\n/g, '{ ').replace(/\n}/g, ' }').replace(/\s\s+/g, ' ') || defaultQuery}"}'`;

  return (
    <Dialog isOpen onClose={onClose} size="large">
      <Dialog.Header>cURL command</Dialog.Header>
      <Dialog.Subtitle>
        Use the following cURL command to interact with your GraphQL Data API. Replace the placeholders with your actual
        values.
      </Dialog.Subtitle>
      <Dialog.Description>
        <div className="flex flex-col gap-4">
          {isLoadingAuthProviderBatch ||
            (!isInitialized && (
              <Center>
                <LoadingSpinner size="medium" />
              </Center>
            ))}
          {isInitialized && (
            <Select
              isFluid
              label="Authentication Provider"
              selectProps={{
                isSearchable: false,
                options: authValues.map((authValue) => ({
                  value: authValue.id,
                  label: `(${authValue.type}) ${authValue.name}${authValue.enabled ? '' : ' (disabled)'}`,
                })),
                value: {
                  value: chosenProviderId,
                  label: chosenProvider
                    ? `(${chosenProvider.type}) ${chosenProvider.name}${chosenProvider.enabled ? '' : ' (disabled)'}`
                    : 'Select an authentication provider',
                },
                onChange: (option) => {
                  if (!isNullish(option)) {
                    setChosenProviderId(option.value);
                  }
                },
              }}
              htmlAttributes={{
                'aria-label': 'Select an authentication provider',
                id: 'auth-provider-select',
              }}
              errorText={errorAuthProviderBatch ? 'Failed to load authentication providers' : undefined}
              className="w-72"
              size="medium"
              type="select"
              data-testid="auth-provider-select"
            ></Select>
          )}
          {errorAuthProviderBatch && <ApiErrorBanner error={errorAuthProviderBatch} hasIcon />}
          {isInitialized && chosenProvider && (
            <TextInput
              key={chosenProvider.id}
              isFluid
              value={chosenProvider.value}
              onChange={(e) => handleUpdateAuthValue(chosenProvider.id, e.target.value)}
              label={chosenProvider.type === DATA_API_AUTH_PROVIDER_TYPE.jwks ? 'JWT (JSON Web Token)' : 'API Key'}
              placeholder={chosenProvider.type === DATA_API_AUTH_PROVIDER_TYPE.jwks ? defaultJWT : defaultAPIKey}
            />
          )}
          {chosenProvider && !chosenProvider.enabled && (
            <Banner type="warning">
              The selected authentication provider '{chosenProvider.name}' is disabled. Requests made with this provider
              will be rejected.
            </Banner>
          )}
          <TextArea
            isFluid
            value={query}
            htmlAttributes={{
              onChange: (e) => setQuery(e.target.value),
            }}
            label="Query"
            placeholder={defaultQuery}
          />
        </div>
      </Dialog.Description>
      <Dialog.Content>
        <RunnableCodeBlock language="bash" code={curlCommand} showCopyIcon />
      </Dialog.Content>
      <Dialog.Actions>
        <Button onClick={onClose}>Done</Button>
      </Dialog.Actions>
    </Dialog>
  );
};
